import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { installGlobalErrorHandlers } from "./lib/errorReporting";
import { observeWebVitals } from "./lib/performanceMonitoring";
import {
  registerVersionedServiceWorker,
  shouldDisableServiceWorkersInCurrentContext,
  storeCurrentVersion,
  stripRuntimeReloadParams,
  unregisterServiceWorkers,
} from "./lib/appVersion";
import { reportVersionTelemetry } from "./lib/versionTelemetry";

// Install global error handlers for centralized error reporting
installGlobalErrorHandlers();
// Start observing Web Vitals (LCP, CLS, Long Tasks)
observeWebVitals();
// Clean cache-busting params from previous versions of the upgrade flow
stripRuntimeReloadParams();
// Stamp current build version
storeCurrentVersion();

// Service worker setup — single source of truth.
// All update detection + reload logic lives in `useServiceWorkerUpdate`,
// mounted via <AppUpdatePrompt />. Here we just register and trigger
// periodic update() calls so the browser pulls a fresh sw.js.
if ("serviceWorker" in navigator) {
  if (shouldDisableServiceWorkersInCurrentContext()) {
    void unregisterServiceWorkers();
  } else {
    const triggerUpdateCheck = async () => {
      const reg = await registerVersionedServiceWorker();
      if (!reg) return;
      try {
        await reg.update();
      } catch {
        // best-effort
      }
    };

    // Handle notification click — open the right page when user taps push notification
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data?.type === "NOTIFICATION_CLICK" && event.data?.url) {
        window.location.href = event.data.url;
      }
    });

    // Initial registration
    void triggerUpdateCheck();

    // Re-check on focus / visibility — fast detection when user returns
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") void triggerUpdateCheck();
    });
    window.addEventListener("focus", () => {
      void triggerUpdateCheck();
    });

    // Periodic check every 20s for fast detection in long-running sessions
    window.setInterval(() => {
      void triggerUpdateCheck();
    }, 20 * 1000);
  }
}

// Report version telemetry (best-effort, non-blocking)
setTimeout(() => reportVersionTelemetry(), 5000);

createRoot(document.getElementById("root")!).render(<App />);
